import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import API from '../lib/api';
import { toast } from 'sonner';

// Pam Golding green sign-in shell (shared @pgp/ui look; tokens vendored in
// src/styles/pgp-ui.css). Honours the server-advertised auth mode: SSO-first
// when authMode==='sso' (local form hidden, break-glass behind a link), or the
// local email/password form when authMode==='local'.
export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginInfo, setLoginInfo] = useState({ authMode: 'local', loginUrl: '', stubMode: false, ready: false });

  useEffect(() => {
    let cancelled = false;
    API.get('/auth/login-info', { params: { redirectUri: '/dashboard' } })
      .then((res) => {
        if (cancelled) return;
        setLoginInfo({
          authMode: res.data.authMode || 'local',
          loginUrl: res.data.loginUrl || '',
          stubMode: !!res.data.stubMode,
          ready: true,
        });
      })
      .catch(() => { if (!cancelled) setLoginInfo((s) => ({ ...s, ready: true })); });
    return () => { cancelled = true; };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      if (user.must_change_password) navigate('/change-password');
      else if (user.role === 'pending') navigate('/pending');
      else navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const showSSO = loginInfo.authMode === 'sso' && loginInfo.loginUrl;
  const showLocal = loginInfo.authMode === 'local';

  const label = { display: 'block', textAlign: 'left', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,.82)', margin: '14px 0 4px' };
  const darkInput = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 6, border: '1px solid rgba(255,255,255,.2)', background: 'rgba(255,255,255,.06)', color: '#fff', fontSize: 14, fontFamily: 'inherit' };
  const linkBtn = { display: 'inline-block', background: 'none', border: 'none', color: 'rgba(255,255,255,.72)', fontSize: 13, textDecoration: 'underline', cursor: 'pointer', marginTop: 16, fontFamily: 'inherit' };
  const hint = { fontSize: 12, color: 'rgba(255,255,255,.6)', margin: '8px 0 0' };

  return (
    <div className="pgp-auth-shell pgp-scope">
      <div className="pgp-auth-shell__inner">
        <img className="pgp-auth-shell__logo" src="/pgp-gates.svg" alt="Pam Golding Properties" />
        <p className="pgp-eyebrow">Pam Golding Properties</p>
        <h1 className="pgp-wordmark">Card Recon</h1>
        <div className="pgp-auth-shell__rule" />
        <p className="pgp-auth-shell__lede">Credit-card reconciliation, GL allocation &amp; Acumatica export.</p>

        {loginInfo.stubMode && (
          <p style={{ margin: '0 0 14px', fontSize: 11, letterSpacing: '.08em', textTransform: 'uppercase', color: '#f0c674' }}>
            Stub mode — dev only
          </p>
        )}

        {showSSO && (
          <>
            <a href={loginInfo.loginUrl} className="pgp-btn pgp-btn--on-dark" style={{ textDecoration: 'none' }} data-testid="login-sso-button">
              Sign in with Alchemy&nbsp;&nbsp;→
            </a>
            <p style={{ ...hint, textAlign: 'center' }}>You'll be redirected to the Pam Golding CRM to sign in.</p>
          </>
        )}

        {showLocal && (
          <form onSubmit={handleLogin} style={{ marginTop: showSSO ? 18 : 0 }}>
            <label style={label} htmlFor="email">Email address</label>
            <input id="email" type="email" style={darkInput} placeholder="you@pamgolding.co.za"
              value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" required
              data-testid="login-email-input" />
            <label style={label} htmlFor="password">Password</label>
            <input id="password" type="password" style={darkInput} placeholder="••••••••"
              value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required
              data-testid="login-password-input" />
            <button type="submit" className="pgp-btn pgp-btn--on-dark" style={{ marginTop: 18 }} disabled={loading} data-testid="login-submit-button">
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
            <p style={{ ...hint, textAlign: 'center' }}>
              Contact your Finance Admin if you need access or have forgotten your password.
            </p>
          </form>
        )}

        {loginInfo.authMode === 'sso' && (
          <div>
            <a href="/break-glass" style={linkBtn}>Break-glass admin sign-in</a>
          </div>
        )}

        <p className="pgp-auth-shell__foot">© 2026 Pam Golding Properties.</p>
      </div>
    </div>
  );
}
